import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProducts } from "@/context/ProductsContext";
import { api, API, formatErr } from "@/lib/api";
import { Plus, Save, Trash2, Search, Edit3, Check, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const abs = (url) => (!url ? url : url.startsWith("http") ? url : url.startsWith("/api/") ? API.replace(/\/api$/, "") + url : url);
const slugify = (s) => (s || "").toLowerCase().trim().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const EMPTY = { id: null, slug: "", name: "", image: "", order: 0, is_active: true };

export default function AdminCategories() {
  const { user } = useAuth();
  const { refresh } = useProducts();
  const [list, setList] = useState([]);
  const [media, setMedia] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [c, m] = await Promise.all([api.get("/admin/categories"), api.get("/media")]);
      setList(c.data); setMedia(m.data);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const filtered = useMemo(() =>
    list.filter(c => !q || (c.name + " " + c.slug).toLowerCase().includes(q.toLowerCase())),
  [list, q]);

  if (user === undefined) return <div className="pt-40 container-lux">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin/categories" replace />;
  if (user.role !== "admin") return (
    <div className="pt-40 container-lux min-h-[60vh]">
      <p className="overline text-muted-foreground">Restricted</p>
      <h1 className="mt-4 font-serif text-4xl">Admin access required</h1>
    </div>
  );

  const openNew = () => setEditing({ ...EMPTY, order: list.length });
  const openEdit = (c) => setEditing({ ...EMPTY, ...c });
  const close = () => setEditing(null);
  const set = (k, v) => setEditing({ ...editing, [k]: v });

  const save = async () => {
    if (!editing) return;
    const slug = editing.slug || slugify(editing.name);
    if (!editing.name) return toast.error("Name is required");
    if (!slug) return toast.error("Slug is required");
    const payload = {
      slug,
      name: editing.name.trim(),
      image: editing.image || "",
      order: Number(editing.order) || 0,
      is_active: editing.is_active !== false,
    };
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/categories/${editing.id}`, payload);
        toast.success("Category updated");
      } else {
        const { data } = await api.post("/admin/categories", payload);
        toast.success("Category created");
        setEditing({ ...EMPTY, ...data });
      }
      await Promise.all([load(), refresh()]);
    } catch (e) { toast.error(formatErr(e)); }
    setSaving(false);
  };

  const del = async () => {
    if (!editing?.id) return;
    if (!confirm(`Delete "${editing.name}"? Products in this category will keep their slug reference.`)) return;
    try { await api.delete(`/admin/categories/${editing.id}`); toast("Category deleted"); close(); await Promise.all([load(), refresh()]); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= list.length) return;
    const a = list[idx], b = list[j];
    try {
      await Promise.all([
        api.put(`/admin/categories/${a.id}`, { ...a, order: j }),
        api.put(`/admin/categories/${b.id}`, { ...b, order: idx }),
      ]);
      await Promise.all([load(), refresh()]);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="admin-categories-page">
      <div className="container-lux">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <p className="overline text-muted-foreground">Atelier · Admin</p>
            <h1 className="mt-4 font-serif tracking-tighter text-5xl md:text-6xl font-light">Categories</h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">Every editorial category on the storefront. Reorder them, rename them, or swap their cover photograph — changes reflect on the homepage grid immediately.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/media" className="lux-btn-outline py-3 px-5 text-[10px]">Media Library</Link>
            <button onClick={openNew} className="lux-btn-dark py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="new-cat-btn"><Plus size={13} strokeWidth={1} /> New Category</button>
          </div>
        </div>

        <div className="mt-12 relative max-w-md">
          <Search size={14} strokeWidth={1} className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search categories" data-testid="cat-search" className="lux-input pl-6" />
        </div>

        <div className="mt-8 border border-border">
          <div className="grid grid-cols-[80px_60px_1fr_180px_100px_100px] gap-4 px-6 py-4 border-b border-border bg-sand/40 overline text-muted-foreground">
            <span>Cover</span><span>Order</span><span>Name</span><span>Slug</span><span>Status</span><span className="text-right pr-2">Actions</span>
          </div>
          {loading ? (
            <p className="px-6 py-10 text-sm text-muted-foreground">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="px-6 py-10 text-sm text-muted-foreground">No categories yet.</p>
          ) : filtered.map((c, idx) => (
            <div key={c.id} className="grid grid-cols-[80px_60px_1fr_180px_100px_100px] gap-4 px-6 py-4 items-center border-b border-border/60 hover:bg-sand/20 transition-colors" data-testid={`cat-row-${c.slug}`}>
              <div className="w-14 h-16 bg-muted overflow-hidden">
                {c.image && <img src={abs(c.image)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex flex-col">
                <span className="text-xs">{c.order}</span>
                <div className="flex gap-0.5 mt-1">
                  <button onClick={()=>move(idx, -1)} disabled={idx===0} className="p-1 disabled:opacity-30" data-testid={`up-${c.slug}`}><ArrowUp size={11} strokeWidth={1} /></button>
                  <button onClick={()=>move(idx, 1)} disabled={idx===filtered.length-1} className="p-1 disabled:opacity-30" data-testid={`down-${c.slug}`}><ArrowDown size={11} strokeWidth={1} /></button>
                </div>
              </div>
              <p className="font-serif text-lg">{c.name}</p>
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{c.slug}</span>
              <span className={`text-[10px] uppercase tracking-widest ${c.is_active ? "text-ink" : "text-muted-foreground"}`}>{c.is_active ? "Live" : "Hidden"}</span>
              <div className="flex justify-end">
                <button onClick={()=>openEdit(c)} className="p-2 hover:bg-muted" title="Edit" data-testid={`edit-cat-${c.slug}`}><Edit3 size={14} strokeWidth={1} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(v)=>!v && close()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-ivory border border-border rounded-none p-0">
          <div className="p-8 border-b border-border sticky top-0 bg-ivory z-10 flex items-center justify-between">
            <div>
              <p className="overline text-muted-foreground">{editing?.id ? "Editing" : "New Category"}</p>
              <DialogHeader><DialogTitle className="font-serif text-3xl md:text-4xl tracking-tighter font-light mt-2">{editing?.name || "Untitled"}</DialogTitle></DialogHeader>
            </div>
            <div className="flex gap-2">
              {editing?.id && <button onClick={del} className="lux-btn-outline py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="delete-cat"><Trash2 size={12} strokeWidth={1} /> Delete</button>}
              <button onClick={close} className="lux-btn-outline py-3 px-5 text-[10px]">Close</button>
              <button onClick={save} disabled={saving} className="lux-btn-dark py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="save-cat"><Save size={12} strokeWidth={1} /> {saving ? "Saving…" : "Save"}</button>
            </div>
          </div>

          {editing && (
          <div className="p-8 grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Field label="Name" value={editing.name} onChange={(e)=>set("name", e.target.value)} onBlur={()=>{ if(!editing.slug) set("slug", slugify(editing.name)); }} testid="c-name" />
              <Field label="Slug" value={editing.slug} onChange={(e)=>set("slug", slugify(e.target.value))} placeholder="lehengas" testid="c-slug" />
              <Field label="Order" type="number" value={editing.order} onChange={(e)=>set("order", e.target.value)} testid="c-order" />
              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" checked={editing.is_active !== false} onChange={(e)=>set("is_active", e.target.checked)} data-testid="c-active" />
                Live on storefront
              </label>
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <label className="overline">Cover Image</label>
                <button type="button" onClick={()=>setShowPicker(true)} className="lux-link" data-testid="c-open-picker">From library →</button>
              </div>
              {editing.image ? (
                <div className="mt-4 aspect-[3/4] bg-muted overflow-hidden border border-border">
                  <img src={abs(editing.image)} alt="" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="mt-4 aspect-[3/4] border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">Pick from the library or paste a URL below.</div>
              )}
              <label className="overline text-muted-foreground mt-6 block">Image URL</label>
              <input value={editing.image} onChange={(e)=>set("image", e.target.value)} placeholder="https://… or /api/media/{id}" className="lux-input" data-testid="c-image-url" />
            </div>
          </div>)}
        </DialogContent>
      </Dialog>

      {/* Media picker */}
      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-ivory border border-border rounded-none">
          <DialogHeader><DialogTitle className="font-serif text-3xl font-light tracking-tighter">Media Library</DialogTitle></DialogHeader>
          {media.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-6">Library empty. <Link to="/admin/media" className="underline">Upload files</Link> first.</p>
          ) : (
            <ul className="mt-6 grid grid-cols-3 md:grid-cols-5 gap-4">
              {media.filter(m => m.content_type?.startsWith("image/")).map(m => {
                const url = `/api/media/${m.id}`;
                const active = editing?.image === url || editing?.image === `${API}/media/${m.id}`;
                return (
                  <button key={m.id} type="button" onClick={()=>{ set("image", url); setShowPicker(false); }}
                    data-testid={`cp-${m.id}`}
                    className={`relative aspect-square overflow-hidden border transition-colors ${active ? "border-ink ring-2 ring-ink" : "border-border hover:border-ink"}`}>
                    <img src={`${API}/media/${m.id}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                    {active && <span className="absolute top-2 right-2 w-6 h-6 bg-ink text-ivory flex items-center justify-center"><Check size={12} strokeWidth={1.5} /></span>}
                  </button>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}</label>
      <input className="lux-input" data-testid={testid} {...rest} />
    </div>
  );
}
